import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Lazy loading для оптимизации bundle
// Критические маршруты (login/register) загружаются сразу для UX
const LoginView = () => import('@/views/LoginView.vue')
const RegisterView = () => import('@/views/RegisterView.vue')

// Основные маршруты - lazy loading
const HomeView = () => import('@/views/HomeView.vue')
const RecipesView = () => import('@/views/RecipesView.vue')
const RecipeDetailView = () => import('@/views/RecipeDetailView.vue')
const RecipeCreateView = () => import('@/views/RecipeCreateView.vue')
const PlannerView = () => import('@/views/PlannerView.vue')
const DayPlannerView = () => import('@/views/DayPlannerView.vue')
const ShoppingView = () => import('@/views/ShoppingView.vue')
const ShoppingListView = () => import('@/views/ShoppingListView.vue')
const StatsView = () => import('@/views/StatsView.vue')
const NotesView = () => import('@/views/NotesView.vue')
const SettingsView = () => import('@/views/SettingsView.vue')
const NotificationsView = () => import('@/views/NotificationsView.vue')
const AdminView = () => import('@/views/AdminView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')

const routes = [
  // Public routes
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { guest: true }
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: { guest: true }
  },
  
  // Protected routes
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { requiresAuth: true }
  },
  {
    path: '/recipes',
    name: 'recipes',
    component: RecipesView,
    meta: { requiresAuth: false } // Рецепты можно просматривать без авторизации
  },
  {
    path: '/recipes/new',
    name: 'recipe-create',
    component: RecipeCreateView,
    meta: { requiresAuth: true }
  },
  {
    path: '/recipes/:id',
    name: 'recipe-detail',
    component: RecipeDetailView,
    meta: { requiresAuth: false }
  },
  {
    path: '/recipes/:id/edit',
    name: 'recipe-edit',
    component: RecipeCreateView,
    meta: { requiresAuth: true }
  },
  {
    path: '/planner',
    name: 'planner',
    component: PlannerView,
    meta: { requiresAuth: true }
  },
  {
    path: '/planner/day/:date?',
    name: 'day-planner',
    component: DayPlannerView,
    meta: { requiresAuth: true }
  },
  {
    path: '/shopping',
    name: 'shopping',
    component: ShoppingView,
    meta: { requiresAuth: true }
  },
  {
    path: '/shopping/:id',
    name: 'shopping-list',
    component: ShoppingListView,
    meta: { requiresAuth: true }
  },
  {
    path: '/stats',
    name: 'stats',
    component: StatsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/notes',
    name: 'notes',
    component: NotesView,
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: NotificationsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    name: 'admin',
    component: AdminView,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  
  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Initialize auth state if needed
  if (authStore.token && !authStore.user) {
    await authStore.fetchProfile()
  }
  
  const isAuthenticated = authStore.isAuthenticated
  
  // Redirect authenticated users away from guest-only pages
  if (to.meta.guest && isAuthenticated) {
    return next({ name: 'home' })
  }
  
  // Redirect unauthenticated users to login
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ 
      name: 'login', 
      query: { redirect: to.fullPath } 
    })
  }
  
  // Check admin access
  if (to.meta.requiresAdmin) {
    const userRole = authStore.user?.role
    if (!['admin', 'developer'].includes(userRole)) {
      return next({ name: 'home' })
    }
  }
  
  next()
})

export default router
